import Geometry from "@arcgis/core/geometry/Geometry";
import { KrookieFormationTypeId } from "../services/reducers/master-plan-modification";

interface BaseKrookie {
  cityEn: { displayValue: string; value: number | null };
  cityAr: string;
  phase: { displayValue: string; value: number | null };
  plotNumber: string;
  oldPlotNumber: string;
  streetNumber: string;
  landUse: { displayValue: string; value: number | null };
  status: { displayValue: string; value: string | null };
  locationStatusCode: { displayValue: string; value: number | null };
  isInvestmentOpportunity: boolean;
  buildArea: string;
  area: string;
  height: string;
  front: string;
  back: string;
  side: string;
  qrCodeLink?: string;
  notes: string[];
}

export interface FormDataEditDetails extends BaseKrookie {
  previousKrookieCode: string;
}

export interface CardKrookies extends BaseKrookie {
  serial: string;
  revision: string;
  wgs: string;
  previousKrookieCode: string;
  drafterName: string | undefined;
  directorName: string;
  signatureDirector: string;
  signatureDrafter: string;
  screenshots: {
    firstScreenshot: { scale: number; url: string };
    secondScreenshot: { scale: number; url: string };
  };
  tableData: TableRow[];
  userPosition?: string;
}

export interface TableRow {
  line: string;
  north: number | null;
  east: number | null;
  distance: string;
}

export enum KrookieStatus {
  Project = 1,
  NeedApproval,
  Rejected,
  Approved,
}

export enum KrookieStatusName {
  Project = "Project",
  NeedApproval = "Need approval",
  Rejected = "Rejected",
  Approved = "Approved",
}

export const KrookieStatusLabels: Record<KrookieStatus, KrookieStatusName> = {
  [KrookieStatus.Project]: KrookieStatusName.Project,
  [KrookieStatus.NeedApproval]: KrookieStatusName.NeedApproval,
  [KrookieStatus.Rejected]: KrookieStatusName.Rejected,
  [KrookieStatus.Approved]: KrookieStatusName.Approved,
};

export interface KrookieDataResponse {
  krookies: Krookie[];
  oldPlots: OldPlot[];
}

export interface HistoryLog {
  isGenerated?: boolean;
  attributes: HistoryLogAttributes;
  changeNum: string;
  changedDateTime: string;
  comment: string | null;
  userId: number;
  userName: string;
  userPosition: string | null;
  id: number;
}

export interface Krookie {
  id: number;
  area: number;
  backSetback: number;
  buildArea: number;
  cityId: number;
  date: string;
  frontSetback: number;
  geometry: string;
  historyLogs: HistoryLog[];
  landUse: number;
  maxScale: number;
  minScale: number;
  minScaleScreenShot: string;
  maxScaleScreenShot: string;
  newPlotNum: number;
  notes: string[];
  oldPlotNumber: string;
  phaseId: number;
  previousKrookieNum: string | null;
  serialNum: string;
  sideSetback: number;
  status: {
    id: number;
    name: string;
  };
  plotStatus: string | null;
  plotLocationStatusCode: number | null;
  plotIsInvOp: number | null;
  streetNum: string;
  maxHeight: string;
  krookieSessionId: number;
  krookieFormationTypeId: KrookieFormationTypeId;
  newPlotId: number;
}

export interface OldPlot {
  plotId: number;
  plotNum: string;
}

export interface HistoryLogAttributes {
  ApproverId: number | null;
  Area: number;
  BackSetback: number;
  BuildArea: number;
  CityId: number;
  Date: string;
  FrontSetback: number;
  KrookieStatusId: number;
  LandUse: number;
  MaxHeight: number | string;
  NewPlotId: number | null;
  NewPlotNum: string;
  Notes: string[];
  PhaseId: number;
  PreviousKrookieNum: string;
  SerialNum: string;
  SideSetback: number;
  StreetNum: string;
}

export enum DocumentOrigin {
  Journal = "journal",
  Edit = "edit",
}

export interface ConvertedKrookie extends CardKrookies {
  id: number;
  sessionId: number;
  statusId: KrookieStatus;
  geometry: Geometry;
  historyLog: HistoryLog[];
  wgs: string;
  userPosition: string;
  documentImage: string | null;
  krookieFormationTypeId: KrookieFormationTypeId;
}

export interface ConvertedKrookieData {
  krookies: Record<number, ConvertedKrookie>;
  oldPlots: OldPlot[];
}

export interface KrookieBackendPayload {
  id?: number;
  statusId?: KrookieStatus;
  serialNum: string;
  date: string;
  cityId: number | null;
  phaseId: number | null;
  newPlotNum: string;
  streetNum: string;
  landUse: number | null;
  geometry: string;
  buildArea: string;
  area: string;
  maxHeight: string;
  frontSetback: string;
  sideSetback: string;
  backSetback: string;
  previousKrookieNum: string;
  notes: string[];
  document: File;
  changeNum: string;
  minScale: number;
  maxScale: number;
  minScaleScreenshot: File;
  maxScaleScreenshot: File;
  logComment: string;
  plotStatus: string | null;
  plotLocationStatusCode: number | null;
  plotIsInvOp: number | null;
}

export interface ResponsePoint {
  index: number;
  x: number;
  y: number;
}

export interface ResponseContour {
  points: ResponsePoint[];
}

export interface ResponseCoordinates {
  wkid?: number;
  contours: ResponseContour[];
}

export interface ObjectDeleteResult {
  success: boolean;
  layerUrl: string;
  deletedIds: number[];
  deletedAttributes: Array<Record<string, any>>;
  error?: any;
}

export interface ChangeKrookieStatusPayload {
  krookieId: string;
  changeNum: string;
  documentBody: File;
  revision: string;
  currentDate: string;
  newPlotId?: string;
}

export type ArcGISFieldType =
  | "small-integer"
  | "integer"
  | "big-integer"
  | "single"
  | "double"
  | "long"
  | "string"
  | "date"
  | "date-only"
  | "time-only"
  | "timestamp-offset"
  | "oid"
  | "geometry"
  | "blob"
  | "raster"
  | "guid"
  | "global-id"
  | "xml";

export interface FieldInfo {
  fieldName: string;
  type: ArcGISFieldType;
  editable: boolean;
  codedValueDict: null | Record<string, string>;
  nullable: boolean;
  defaultValue: number | string;
  maxLength: number;
}
